"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

import { useLazyGetCustomLayerBoundaryGeometriesQuery, useLazyGetCustomLayerDistrictBoundaryGeometriesQuery } from "@/providers/store/api/custom-layers/custom-layers";
import { useLazyGetDistrictBoundaryPreviewGeometriesQuery } from "@/providers/store/api/districts/districts";
import { useLazyGetMapLevelBoundaryGeometriesQuery } from "@/providers/store/api/map-levels/map-levels";
import { useLazyGetAttendanceBoundaryPreviewGeometriesQuery } from "@/providers/store/api/schools/schools";
import type { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";
import type { IBoundaryGeometriesPageResponse } from "@/shared/interfaces/boundary-geometry.interface";

export type BoundariesEditGeometryCacheStatus = "idle" | "loading" | "ready";

export type BoundariesEditGeometryLoadScope =
  | { kind: "customSchool"; districtId: string; layerId: string }
  | { kind: "customDistrict"; districtId: string; layerId: string }
  | {
      kind: "attendancePreview";
      districtId: string;
      documentIds: string[];
      schoolLevelType: SchoolLevelType;
    }
  | { kind: "districtPreview"; districtId: string; documentId: string }
  | { kind: "mapLevelPreview"; districtId: string; levelId: string };

export interface BoundariesEditGeometryCacheContextValue {
  status: BoundariesEditGeometryCacheStatus;
  loadedCount: number;
  totalCount: number;
  isGeometryLoading: boolean;
  getGeometry: (id: string) => string | null;
  startLoad: (scope: BoundariesEditGeometryLoadScope) => void;
  abortAndClear: () => void;
  clear: () => void;
}

const BoundariesEditGeometryCacheContext =
  createContext<BoundariesEditGeometryCacheContextValue | null>(null);

const PAGE_LIMIT = 100;
/** Transient network failures only — never hammer the API after HTTP 4xx/5xx. */
const MAX_TRANSIENT_RETRIES = 2;
const RETRY_DELAY_MS = 500;
/** Keep soft-lock overlay/progress visible briefly so fast loads don't flash. */
const MIN_LOADING_VISIBLE_MS = 1000;

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timeoutId = window.setTimeout(() => resolve(), ms);
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (typeof error === "object" &&
      error != null &&
      "name" in error &&
      (error as { name?: string }).name === "AbortError")
  );
}

/** RTK Query / fetch transport failures that may succeed on a short retry. */
function isTransientFetchError(error: unknown): boolean {
  if (typeof error !== "object" || error == null || !("status" in error)) {
    return false;
  }

  const status = (error as { status?: string | number }).status;
  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
}

function isLoadStale(
  signal: AbortSignal,
  generation: number,
  currentGeneration: number,
): boolean {
  return signal.aborted || generation !== currentGeneration;
}

interface BoundaryGeometriesPageRequest {
  unwrap: () => Promise<IBoundaryGeometriesPageResponse>;
  abort?: () => void;
}

async function unwrapPageWithAbort(
  request: BoundaryGeometriesPageRequest,
  signal: AbortSignal,
): Promise<IBoundaryGeometriesPageResponse> {
  if (signal.aborted) {
    request.abort?.();
    throw new DOMException("Aborted", "AbortError");
  }

  const onAbort = () => request.abort?.();
  signal.addEventListener("abort", onAbort, { once: true });
  try {
    return await request.unwrap();
  } finally {
    signal.removeEventListener("abort", onAbort);
  }
}

async function fetchPageWithTransientRetries(
  fetchPage: () => BoundaryGeometriesPageRequest,
  signal: AbortSignal,
): Promise<IBoundaryGeometriesPageResponse> {
  let transientAttempts = 0;

  while (!signal.aborted) {
    try {
      return await unwrapPageWithAbort(fetchPage(), signal);
    } catch (error) {
      if (signal.aborted || isAbortError(error)) {
        throw new DOMException("Aborted", "AbortError");
      }

      if (isTransientFetchError(error) && transientAttempts < MAX_TRANSIENT_RETRIES) {
        transientAttempts += 1;
        await sleep(RETRY_DELAY_MS, signal);
        continue;
      }

      // HTTP 4xx/5xx (and exhausted network retries): stop — do not spam the API.
      throw error;
    }
  }

  throw new DOMException("Aborted", "AbortError");
}

async function waitForMinLoadingVisible(
  startedAt: number,
  signal: AbortSignal,
): Promise<void> {
  const remaining = MIN_LOADING_VISIBLE_MS - (Date.now() - startedAt);
  if (remaining > 0) {
    await sleep(remaining, signal);
  }
}

function mergePageItemsIntoCache(
  cache: Map<string, string>,
  items: IBoundaryGeometriesPageResponse["items"],
): void {
  for (const item of items) {
    cache.set(item.id, item.geometryGeoJson);
  }
}

export function useBoundariesEditGeometryCache() {
  const context = useContext(BoundariesEditGeometryCacheContext);
  if (!context) {
    throw new Error(
      "useBoundariesEditGeometryCache must be used inside <BoundariesEditGeometryCacheProvider />",
    );
  }
  return context;
}

export const BoundariesEditGeometryCacheProvider: FC<PropsWithChildren> = ({ children }) => {
  const [status, setStatus] = useState<BoundariesEditGeometryCacheStatus>("idle");
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const cacheRef = useRef<Map<string, string>>(new Map());
  const abortRef = useRef<AbortController | null>(null);
  const loadGenerationRef = useRef(0);
  const loadStartedAtRef = useRef(0);

  const [fetchCustomSchoolPage] = useLazyGetCustomLayerBoundaryGeometriesQuery();
  const [fetchCustomDistrictPage] = useLazyGetCustomLayerDistrictBoundaryGeometriesQuery();
  const [fetchAttendancePage] = useLazyGetAttendanceBoundaryPreviewGeometriesQuery();
  const [fetchDistrictPreviewPage] = useLazyGetDistrictBoundaryPreviewGeometriesQuery();
  const [fetchMapLevelPreviewPage] = useLazyGetMapLevelBoundaryGeometriesQuery();

  const clearCacheState = useCallback(() => {
    cacheRef.current = new Map();
    setLoadedCount(0);
    setTotalCount(0);
    setStatus("idle");
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    loadGenerationRef.current += 1;
    clearCacheState();
  }, [clearCacheState]);

  const abortAndClear = useCallback(() => {
    clear();
  }, [clear]);

  const getGeometry = useCallback((id: string) => {
    return cacheRef.current.get(id) ?? null;
  }, []);

  const fetchPageForScope = useCallback(
    (
      scope: BoundariesEditGeometryLoadScope,
      cursor: string | undefined,
    ): BoundaryGeometriesPageRequest => {
      const pageArgs = {
        cursor,
        limit: PAGE_LIMIT,
      };

      switch (scope.kind) {
        case "customSchool":
          return fetchCustomSchoolPage({
            districtId: scope.districtId,
            layerId: scope.layerId,
            ...pageArgs,
          });
        case "customDistrict":
          return fetchCustomDistrictPage({
            districtId: scope.districtId,
            layerId: scope.layerId,
            ...pageArgs,
          });
        case "attendancePreview":
          return fetchAttendancePage({
            districtId: scope.districtId,
            documentIds: scope.documentIds,
            schoolLevelType: scope.schoolLevelType,
            ...pageArgs,
          });
        case "districtPreview":
          return fetchDistrictPreviewPage({
            districtId: scope.districtId,
            documentId: scope.documentId,
            ...pageArgs,
          });
        case "mapLevelPreview":
          return fetchMapLevelPreviewPage({
            districtId: scope.districtId,
            levelId: scope.levelId,
            ...pageArgs,
          });
        default: {
          const _exhaustive: never = scope;
          return _exhaustive;
        }
      }
    },
    [
      fetchAttendancePage,
      fetchCustomDistrictPage,
      fetchCustomSchoolPage,
      fetchDistrictPreviewPage,
      fetchMapLevelPreviewPage,
    ],
  );

  const runLoad = useCallback(
    async (scope: BoundariesEditGeometryLoadScope, signal: AbortSignal, generation: number) => {
      let cursor: string | undefined;
      let loaded = 0;

      cacheRef.current = new Map();
      setLoadedCount(0);
      setTotalCount(0);
      loadStartedAtRef.current = Date.now();
      setStatus("loading");

      const finishReady = async () => {
        await waitForMinLoadingVisible(loadStartedAtRef.current, signal);
        if (isLoadStale(signal, generation, loadGenerationRef.current)) {
          return;
        }
        setStatus("ready");
      };

      while (!signal.aborted) {
        const page = await fetchPageWithTransientRetries(
          () => fetchPageForScope(scope, cursor),
          signal,
        );

        if (isLoadStale(signal, generation, loadGenerationRef.current)) {
          return;
        }

        setTotalCount(page.totalCount);

        if (page.totalCount === 0) {
          setLoadedCount(0);
          await finishReady();
          return;
        }

        mergePageItemsIntoCache(cacheRef.current, page.items);
        loaded += page.items.length;
        setLoadedCount(Math.min(loaded, page.totalCount));

        if (page.nextCursor == null) {
          await finishReady();
          return;
        }

        cursor = page.nextCursor;
      }
    },
    [fetchPageForScope],
  );

  const startLoad = useCallback(
    (scope: BoundariesEditGeometryLoadScope) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const generation = loadGenerationRef.current + 1;
      loadGenerationRef.current = generation;

      runLoad(scope, controller.signal, generation).catch((error) => {
        if (generation !== loadGenerationRef.current) {
          return;
        }
        if (isAbortError(error) || controller.signal.aborted) {
          clearCacheState();
          return;
        }
        clearCacheState();
      });
    },
    [clearCacheState, runLoad],
  );

  const value = useMemo<BoundariesEditGeometryCacheContextValue>(
    () => ({
      status,
      loadedCount,
      totalCount,
      isGeometryLoading: status === "loading",
      getGeometry,
      startLoad,
      abortAndClear,
      clear,
    }),
    [abortAndClear, clear, getGeometry, loadedCount, startLoad, status, totalCount],
  );

  return (
    <BoundariesEditGeometryCacheContext.Provider value={value}>
      {children}
    </BoundariesEditGeometryCacheContext.Provider>
  );
};
